import type { GQLConsentEvent } from '../../../../hooks/graphql/contacts/useContactConsentGQL';
import type { ConsentChannel, ConsentEventType, ConsentHistoryEvent } from './consent.types';

/** A team member the ledger's `actor` id resolves to. */
export interface ResolvedUser {
  name: string;
  role: string | null;
}

const CHANNEL_BY_LEDGER_VALUE: Record<string, ConsentChannel> = {
  voice: 'phone',
  call: 'phone',
  phone: 'phone',
  sms: 'sms',
  text: 'sms',
  email: 'email',
};

const EVENT_TYPE_BY_LEDGER_VALUE: Record<string, ConsentEventType> = {
  opt_in: 'optin',
  opt_out: 'optout',
  import: 'imported',
  imported: 'imported',
  consent_source_changed: 'modified',
  help: 'modified',
};

const CHANNEL_TITLE: Record<ConsentChannel, string> = {
  phone: 'Phone',
  sms: 'SMS',
  email: 'Email',
};

const EVENT_TITLE: Record<ConsentEventType, string> = {
  optin: 'Opt-In',
  optout: 'Opt-Out',
  modified: 'Consent Modified',
  imported: 'Consent Imported',
};

/**
 * The ledger stores `source` as a machine token. These are the tokens the
 * write paths emit today; anything else falls back to a humanized token.
 */
const SOURCE_LABEL: Record<string, string> = {
  dashboard: 'Manual update',
  twilio_webhook: 'Inbound keyword',
  sendblue_webhook: 'Inbound keyword',
  crm_sync: 'CRM sync',
  import: 'Import',
  db_upload: 'Database upload',
  idx_signup: 'Website form',
  website: 'Website form',
  remi: 'Remi',
  admin: 'Admin',
};

/**
 * Roles render parenthesised after the actor name, so a roster role token
 * (`org_admin`) is capitalised to match the hand-written fallbacks below.
 */
function roleLabel(role: string | null): string | null {
  if (!role) return null;
  const spaced = role.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : null;
}

/**
 * Fallback attribution for events with no resolvable actor — webhooks, syncs
 * and imports have no team member behind them, and `source` is what says who
 * acted. Human actors are resolved from the ledger's `actor` id instead.
 */
const ACTOR_BY_SOURCE: Record<string, { actor: string; role: string }> = {
  dashboard: { actor: 'Team member', role: 'Agent' },
  admin: { actor: 'Team member', role: 'Agent' },
  twilio_webhook: { actor: 'Lead', role: 'Self-service' },
  sendblue_webhook: { actor: 'Lead', role: 'Self-service' },
  idx_signup: { actor: 'Lead', role: 'Self-service' },
  website: { actor: 'Lead', role: 'Self-service' },
  remi: { actor: 'Remi', role: 'AI' },
  crm_sync: { actor: 'System', role: 'Sync' },
  import: { actor: 'System', role: 'Migration' },
  db_upload: { actor: 'System', role: 'Upload' },
};

function humanize(token: string): string {
  const spaced = token.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : token;
}

/** Shared with the channel cards, which render their `lastEvent` the same way. */
export function mapConsentEvent(
  dto: GQLConsentEvent,
  actors: Record<string, ResolvedUser>
): ConsentHistoryEvent | null {
  const channel = CHANNEL_BY_LEDGER_VALUE[dto.channel?.toLowerCase() ?? ''];
  if (!channel) return null;

  const rawType = dto.eventType?.toLowerCase() ?? '';
  // consentState is the authoritative in/out signal when the type is unmapped.
  const type =
    EVENT_TYPE_BY_LEDGER_VALUE[rawType] ??
    (dto.consentState === true ? 'optin' : dto.consentState === false ? 'optout' : 'modified');

  const sourceToken = dto.source?.toLowerCase() ?? '';
  const fallback = ACTOR_BY_SOURCE[sourceToken];
  // Rows written before SPEC-2314 still carry a Firebase uid, which matches no
  // roster entry — those fall back to source attribution.
  const resolved = dto.actor ? actors[dto.actor] : undefined;

  return {
    id: dto.consentEventId,
    channel,
    type,
    title: `${CHANNEL_TITLE[channel]} ${EVENT_TITLE[type]}`,
    source: SOURCE_LABEL[sourceToken] ?? (dto.source ? humanize(dto.source) : 'Unknown source'),
    actor: resolved?.name ?? fallback?.actor ?? 'System',
    actorRole: resolved ? roleLabel(resolved.role) : (fallback?.role ?? null),
    note: dto.reason?.trim() || null,
    occurredAt: dto.occurredAt ?? dto.createdAt ?? '',
  };
}

/** Resolver already returns newest-first; unmappable channels are dropped. */
export function mapConsentHistory(
  events: GQLConsentEvent[],
  actors: Record<string, ResolvedUser> = {}
): ConsentHistoryEvent[] {
  return events
    .map((e) => mapConsentEvent(e, actors))
    .filter((e): e is ConsentHistoryEvent => e !== null);
}
